import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Field, Form } from 'react-final-form';

import colors from '@front/common/src/utils/colors';
import { required } from '@front/common/src/forms/validations';
import { useGetConfigQuery } from '@front/common/src/queries/config-queries';
import { ProAccount } from '@front/common/src/types/commons';
import { isCountryFR } from '@front/common/src/utils/country';

import { useCreateAccount } from '@front/pro/src/queries/account-queries';

import { translate } from '@front/i18n';

import Alert from '@front/web-ui/src/components/alert/alert';
import Switch from '@front/web-ui/src/components/switch/switch';
import Button from '@front/web-ui/src/components/button/button';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import { SelectAdapter } from '@front/web-ui/src/forms/adapters';
import { SelectProps } from '@front/web-ui/src/components/select/select';
import Checkbox from '@front/web-ui/src/components/checkbox/checkbox';
import SimpleLink from '@front/web-ui/src/components/link/simple-link';
import Separator from '@front/web-ui/src/components/separator/separator';

import { Screen } from '../../components/screen/screen';
import ButtonBar from '../../components/button-bar/button-bar';
import { route } from '../../routes/routes';
import { useStepper } from '../../routes/use-stepper';
import {
  useAdditionalInformation,
  useSubscribedPro,
  useUserInformation,
  useWorkingInformation,
  WorkingInformationFR,
} from '../../contexts/signup-form-context';
import { ConditionalField } from '../../components/conditional-field/conditional-field';
import { useNavigateHistoryWithQuery } from '../../hooks/use-navigate-history-with-query';
import DisponibilitySlider from '../../components/disponibility-label/disponibility-slider';
import { useIsUserFromIndeed } from '../../hooks/use-is-user-from-indeed';
import {
  logSignupConfirmation,
  logSignupStepEvent,
  setAmplitudeUserId,
  setUserProperties,
} from '../../utils/tracking';
import { numberToWeeklyHours } from '../../utils/utils';

import CGU from './cgu';

const t = translate('screens.additional_information');

const StyledAlert = styled(Alert)`
  margin-bottom: 20px;
`;

const StyledSwitchLabel = styled.label`
  display: flex;
  margin-top: 30px;
  gap: 15px;
`;

const StyledSelect = styled(SelectAdapter)`
  margin-top: 20px;
`;

const Divider = styled(Separator)<{ $isChildcare: boolean }>`
  margin-top: ${({ $isChildcare }) => ($isChildcare ? '10px' : '20px')};
  margin-bottom: 0;
`;

const StyledTextWithLink = styled.div`
  > p {
    display: inline;
    margin: 0;
  }
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 5px;
`;

const isProQualified = (proAccount: ProAccount) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { status_detail, pro_status } = proAccount;
  if (!status_detail || !pro_status) return false;
  return ['New', 'Active'].includes(pro_status) && ['Qualified', 'Onboarding Test'].includes(status_detail);
};

const useDeclarativeSource = () => {
  const commonDeclarativeSourceOptions = useMemo(
    () => [
      { name: t('.declarative_source_dropdown.word_of_mouth'), value: 'word_of_mouth' },
      { name: t('.declarative_source_dropdown.sponsorship'), value: 'sponsorship' },
      { name: t('.declarative_source_dropdown.google'), value: 'google' },
      { name: t('.declarative_source_dropdown.facebook'), value: 'facebook' },
      { name: t('.declarative_source_dropdown.instagram'), value: 'instagram' },
      { name: t('.declarative_source_dropdown.mailing'), value: 'mailing' },
      { name: t('.declarative_source_dropdown.email'), value: 'email' },
      { name: t('.declarative_source_dropdown.ad_display'), value: 'ad_display' },
      { name: t('.declarative_source_dropdown.press'), value: 'press' },
      { name: t('.declarative_source_dropdown.facebook_groups'), value: 'facebook_groups' },
      { name: t('.declarative_source_dropdown.app_store'), value: 'app_store' },
      { name: t('.declarative_source_dropdown.other'), value: 'other' },
    ],
    [],
  );

  const declarativeSourceOptionsFR: SelectProps['options'] = useMemo(
    () => [
      { name: t('.declarative_source_dropdown.job_board'), value: 'job_board' },
      { name: t('.declarative_source_dropdown.ad_tv'), value: 'ad_tv' },
    ],
    [],
  );

  return {
    declarativeSourceOptions: [
      ...commonDeclarativeSourceOptions,
      ...(isCountryFR() ? declarativeSourceOptionsFR : []),
    ],
  };
};

const useRecurringChildcare = () => {
  const recurringChildcareOptions: SelectProps['options'] = useMemo(
    () => [
      { name: t('.childcare_dropdown.recurring_only'), value: 'recurring_only' },
      { name: t('.childcare_dropdown.recurring_or_spot'), value: 'recurring_or_spot' },
      { name: t('.childcare_dropdown.spot_only'), value: 'spot_only' },
    ],
    [],
  );

  return { recurringChildcareOptions };
};

const AdditionalInformation = () => {
  const location = useLocation();
  const { historyPushWithQuery } = useNavigateHistoryWithQuery();
  const { mutate: createAccount, isLoading, error } = useCreateAccount();
  const { additionalInformation } = useAdditionalInformation();
  const { workingInformation } = useWorkingInformation();
  const { nextRoute } = useStepper();
  const { userInformation } = useUserInformation();
  const { recurringChildcareOptions } = useRecurringChildcare();
  const { declarativeSourceOptions } = useDeclarativeSource();
  const { setSubscribedPro } = useSubscribedPro();
  const isUserFromIndeed = useIsUserFromIndeed();
  const { config } = useGetConfigQuery();

  useEffect(() => {
    // In some browser, focus on the input is not enough to view the title of the page so we are scrolling to the top
    // eslint-disable-next-line no-multi-assign
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  const onSubmit = useCallback(
    // @ts-ignore TODO: set typing
    (values) => {
      const searchParams = new URLSearchParams(location.search);
      const utmSource = searchParams.get('utm_source');
      // Quick fix to solve the city field missmatch waiting for better api contract in backend side
      // If user has a query params `city` in the url Rails will rather take the value in the query params rahter than
      // the one in the payload. Resulting in missmatching city for pros (cf: BUG-1157)
      searchParams.delete('city');
      const newSearchParams = searchParams.toString();
      createAccount(
        {
          body: {
            utm_source: utmSource || undefined,
            device_source: 'web',
            is_multi_step: true,
            universe: window.wecasa.universe,
            ...userInformation,
            ...workingInformation,
            ...values,
            weekly_hours: numberToWeeklyHours(values.weekly_hours),
            is_revenue_simulator: !isUserFromIndeed,
          },
          search: newSearchParams ? `?${searchParams.toString()}` : undefined,
        },
        {
          onSuccess: (response) => {
            setSubscribedPro(response);
            ['wecasa-userInformation', 'wecasa-workingInformation'].forEach((key) => localStorage.removeItem(key));
            if (isProQualified(response.pro_account)) {
              setAmplitudeUserId(response.pro_account.public_id);
              setUserProperties({
                pro_status: response.pro_account.pro_status,
                pro_status_detail: response.pro_account.status_detail,
              });
              logSignupStepEvent({
                step: nextRoute,
                account: {
                  ...values,
                  weekly_hours: numberToWeeklyHours(values.weekly_hours),
                  company_status: isCountryFR()
                    ? (workingInformation as WorkingInformationFR)?.company_status
                    : undefined,
                  nova_status: isCountryFR() ? (workingInformation as WorkingInformationFR)?.nova_status : undefined,
                },
                universe: 'cleaning',
              });
              logSignupConfirmation({
                universe: 'cleaning',
                account: {
                  company_status: isCountryFR()
                    ? (workingInformation as WorkingInformationFR)?.company_status
                    : undefined,
                  nova: isCountryFR() ? (workingInformation as WorkingInformationFR)?.nova_status : undefined,
                  declarative_source: values.declarative_source,
                  weekly_hours: numberToWeeklyHours(values.weekly_hours),
                  also_a_nanny: values.also_a_nanny,
                  childcare_availability: values.childcare_availability,
                },
              });
              historyPushWithQuery(route(nextRoute));
            } else {
              const disqualificationReason = response.pro_account.disqualification_reason;
              const queryParamList = [{ paramKey: 'reason', paramValue: disqualificationReason }];
              historyPushWithQuery(route('not_qualified'), queryParamList);
            }
          },
        },
      );
    },
    [
      createAccount,
      historyPushWithQuery,
      isUserFromIndeed,
      location.search,
      nextRoute,
      setSubscribedPro,
      userInformation,
      workingInformation,
    ],
  );

  return (
    <Screen title={t('.title', { name: userInformation?.first_name || '' })} isCenteredOnDesktop={false}>
      {error && <StyledAlert type='error' content={error} />}
      <Form
        onSubmit={onSubmit}
        initialValues={{
          weekly_hours: additionalInformation?.weekly_hours || '20',
          also_a_nanny: isCountryFR() ? additionalInformation?.also_a_nanny || false : undefined,
          internet_promotion_authorisation: additionalInformation?.internet_promotion_authorisation || true,
          childcare_availability: additionalInformation?.childcare_availability,
          declarative_source: additionalInformation?.declarative_source,
        }}
        mutators={{
          setValue: (args, state, utils) => {
            utils.changeValue(state, 'weekly_hours', () => args[1]);
          },
        }}
      >
        {({ values, handleSubmit, form, errors }) => (
          <form onSubmit={handleSubmit}>
            <DisponibilitySlider
              id='weekly_hours'
              name='weekly_hours'
              min={5}
              max={45}
              step={5}
              hours={values.weekly_hours}
              initialValue={additionalInformation?.weekly_hours ? +additionalInformation.weekly_hours : 20}
            />
            {errors?.weekly_hours && <Text color={colors.error100}>{errors.weekly_hours}</Text>}
            {isCountryFR() && (
              <>
                <StyledSwitchLabel htmlFor='also_a_nanny'>
                  <ConditionalField type='checkbox' id='also_a_nanny' name='also_a_nanny' changeValue={form.change}>
                    {({ input }) => (
                      <Switch
                        id='also_a_nanny'
                        name='also_a_nanny'
                        isChecked={!!input.checked}
                        onChange={input.onChange}
                      />
                    )}
                  </ConditionalField>
                  <Text>{t('.childcare_switch_label')}</Text>
                </StyledSwitchLabel>
                {values.also_a_nanny && (
                  <ConditionalField
                    validate={required('childcare_availability')}
                    component={StyledSelect}
                    name='childcare_availability'
                    changeValue={form.change}
                    id='childcare_availability'
                    label={t('.childcare_dropdown.placeholder')}
                    options={recurringChildcareOptions}
                  />
                )}
              </>
            )}
            <Divider $isChildcare={!!values.also_a_nanny} />
            <Field
              validate={required('declarative_source')}
              component={StyledSelect}
              name='declarative_source'
              id='declarative_source'
              label={t('.declarative_source_dropdown.placeholder')}
              options={declarativeSourceOptions}
            />
            <Field type='checkbox' name='internet_promotion_authorisation' id='internet_promotion_authorisation'>
              {({ input }) => (
                <StyledCheckboxLabel htmlFor='internet_promotion_authorisation'>
                  <Checkbox
                    name='internet_promotion_authorisation'
                    id='internet_promotion_authorisation'
                    checked={!!input.checked}
                    onChange={input.onChange}
                  />
                  <StyledTextWithLink>
                    <Text htmlTag='p'>{t('.internet_promotion_label_1')}</Text>
                    <SimpleLink openInNewTab href='https://www.wecasa.fr/autorisation-promotion-internet'>
                      {t('.internet_promotion_label_2')}
                    </SimpleLink>
                  </StyledTextWithLink>
                </StyledCheckboxLabel>
              )}
            </Field>
            <CGU config={config} />
            <ButtonBar>
              <Button size='lg' type='submit' isLoading={isLoading} isDisabled={isLoading}>
                {t('base.next')}
              </Button>
            </ButtonBar>
          </form>
        )}
      </Form>
    </Screen>
  );
};

export default AdditionalInformation;
